/* import __COLOCATED_TEMPLATE__ from './oauth-editor.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency-decorators';
import { inject as service } from '@ember/service';
import { post } from 'embercom/lib/ajax';
import resolveObjectPath from 'embercom/lib/resolve-object-path';
import type IntlService from 'ember-intl/services/intl';
import type Store from '@ember-data/store';
import type Token from 'embercom/models/custom-authentication/token';
import { taskFor } from 'ember-concurrency-ts';
import { RequestFormat } from 'embercom/models/custom-authentication/oauth-token-configuration';
import { action } from '@ember/object';
// @ts-ignore
import type NotificationsService from 'embercom/services/notifications-service';

interface Args {
  customAuthenticationToken: Token;
  form: any;
}

export default class OauthEditor extends Component<Args> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare appService: AppService;
  // @ts-ignore
  @service declare notificationsService: NotificationsService;

  @tracked customAuthenticationToken = this.args.customAuthenticationToken;
  @tracked declare testResponse: any;
  @tracked bannerText = '';
  @tracked declare bannerType: 'confirmation' | 'error' | 'warning';
  @tracked declare displayableResponseBody: string | null;
  @tracked declare foundSecretInResponse: boolean | null;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    // Initialize requestFormat if it's not set or invalid
    let currentValue: string = this.customAuthenticationToken.oauthTokenConfiguration.requestFormat;
    if (!currentValue || !Object.values(RequestFormat).includes(currentValue as RequestFormat)) {
      this.customAuthenticationToken.oauthTokenConfiguration.requestFormat = RequestFormat.JSON;
    }
  }

  get responseTokenPath() {
    return 'access_token';
  }

  get requestFormatOptions() {
    return [
      {
        text: 'JSON',
        value: RequestFormat.JSON,
        description: this.intl.t(
          'settings.custom-authentication-tokens.new.oauth.request-format-json-description',
        ),
      },
      {
        // eslint-disable-next-line @intercom/intercom/no-bare-strings
        text: 'Form Encoded',
        value: RequestFormat.FORM_ENCODED,
        description: this.intl.t(
          'settings.custom-authentication-tokens.new.oauth.request-format-form-encoded-description',
        ),
      },
    ];
  }

  get selectedRequestFormat() {
    return this.customAuthenticationToken.oauthTokenConfiguration.requestFormat;
  }

  get requestHeaderValue() {
    let contentType = this.customAuthenticationToken.oauthTokenConfiguration.contentType;
    // eslint-disable-next-line @intercom/intercom/no-bare-strings
    return `Content-Type: ${contentType}`;
  }

  get requestBodyValue() {
    let maskedSecret = '*'.repeat(
      this.customAuthenticationToken.oauthTokenConfiguration.clientSecret?.length || 0,
    );
    let bodyParams: Record<string, string> = {
      grant_type: 'client_credentials',
      client_id: this.customAuthenticationToken.oauthTokenConfiguration.clientId || '',
      client_secret: maskedSecret,
    };

    let scope = this.customAuthenticationToken.oauthTokenConfiguration.scope;
    if (scope && scope.trim()) {
      bodyParams.scope = scope;
    }

    if (this.selectedRequestFormat === RequestFormat.JSON) {
      return JSON.stringify(bodyParams, null, 2);
    } else {
      let params = Object.entries(bodyParams)
        .map(([key, value]) => `${key}=${value}`)
        .join('&\n');
      return params;
    }
  }

  @action
  setRequestFormat(format: RequestFormat) {
    this.customAuthenticationToken.oauthTokenConfiguration.requestFormat = format;
  }

  @dropTask
  *testRequest() {
    let params = {
      app_id: this.appService.app.id,
      custom_authentication_token_id: this.customAuthenticationToken.id,
      oauth_token_configuration: {
        url: this.customAuthenticationToken.oauthTokenConfiguration.url,
        client_id: this.customAuthenticationToken.oauthTokenConfiguration.clientId,
        client_secret: this.customAuthenticationToken.oauthTokenConfiguration.clientSecret,
        scope: this.customAuthenticationToken.oauthTokenConfiguration.scope,
        request_format: this.customAuthenticationToken.oauthTokenConfiguration.requestFormat,
      },
    };

    try {
      this._setLatestTestRequestParams();
      this.customAuthenticationToken.oauthTokenConfiguration.mostRecentTestPassed = false; // to invalidate previous result
      // @ts-ignore
      this.testResponse = yield post(
        '/ember/custom_authentication/tokens/test_oauth_token',
        params,
      );
      this.displayableResponseBody = JSON.stringify(this.responseBody, null, 2);
      this.customAuthenticationToken.tokenValue = resolveObjectPath(
        this.responseBody,
        this.responseTokenPath,
      );
      this.foundSecretInResponse = !!resolveObjectPath(this.responseBody, this.responseTokenPath);
      this._setBannerParams();
    } catch (exception) {
      this.displayableResponseBody = null;
      this.foundSecretInResponse = false;
      this._setBannerOnException(exception);
    }
  }

  get isTestRequestRunning() {
    return taskFor(this.testRequest).isRunning;
  }

  _setLatestTestRequestParams() {
    this.customAuthenticationToken.oauthTokenConfiguration.latestTestRequestParams =
      this.store.createFragment('custom-authentication/oauth-token-configuration', {
        url: this.customAuthenticationToken.oauthTokenConfiguration.url,
        clientId: this.customAuthenticationToken.oauthTokenConfiguration.clientId,
        clientSecret: this.customAuthenticationToken.oauthTokenConfiguration.clientSecret,
        scope: this.customAuthenticationToken.oauthTokenConfiguration.scope,
        requestFormat: this.customAuthenticationToken.oauthTokenConfiguration.requestFormat,
      });
  }

  _setBannerParams() {
    this.bannerText = '';
    if (this.testResponse.refresh_response.success && this.foundSecretInResponse) {
      this.customAuthenticationToken.oauthTokenConfiguration.mostRecentTestPassed = true;
      this.bannerType = 'confirmation';
      this.bannerText = this.intl.t(
        'settings.custom-authentication-tokens.new.oauth.success-with-token',
        {
          status: this.testResponse.refresh_response.status,
          return_code: this.testResponse.refresh_response.return_code.toUpperCase(),
          path: this.responseTokenPath,
        },
      );
    } else if (this.testResponse.refresh_response.success && !this.foundSecretInResponse) {
      this.customAuthenticationToken.oauthTokenConfiguration.mostRecentTestPassed = false;
      this.bannerType = 'warning';
      this.bannerText = this.intl.t(
        'settings.custom-authentication-tokens.new.oauth.success-without-token',
        {
          status: this.testResponse.refresh_response.status,
          return_code: this.testResponse.refresh_response.return_code.toUpperCase(),
          path: this.responseTokenPath,
        },
      );
    } else {
      this.customAuthenticationToken.oauthTokenConfiguration.mostRecentTestPassed = false;
      this.bannerType = 'error';
      this.bannerText +=
        this.testResponse.refresh_response.human_readable_error ||
        this.intl.t('settings.custom-authentication-tokens.new.response.error');
    }
  }

  _setBannerOnException(exception: any) {
    this.bannerType = 'error';
    this.bannerText = exception.errorThrown;
  }

  get responseBody() {
    return this.testResponse?.refresh_response.body;
  }

  get oauthTokenUrlValidation() {
    return this.customAuthenticationToken.oauthTokenConfiguration.validations.attrs.url;
  }

  get clientIdValidation() {
    return this.customAuthenticationToken.oauthTokenConfiguration.validations.attrs.clientId;
  }

  get clientSecretValidation() {
    return this.customAuthenticationToken.oauthTokenConfiguration.validations.attrs.clientSecret;
  }

  get isTestButtonDisabled() {
    // Disable if OAuth config is invalid
    return !this.customAuthenticationToken.oauthTokenConfiguration.validations.isValid;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Settings::CustomAuthentication::OauthEditor': typeof OauthEditor;
    'settings/custom-authentication/oauth-editor': typeof OauthEditor;
  }
}
